import { getAbsoluteUrl, getConfig } from '@sydle/ui-utils';
import type { UIAppConfig } from '../types/ui-app-config';

export default async function (): Promise<void> {
  /**
   * If the url points to the root, but does not have a trailing slash,
   * vaadin will ignore the navigation as the url will not match the baseUrl.
   *
   * Example:
   *
   * The url "https://sydle.com/loja" will be ignored by Vaadin, because the baseUrl for this
   * application would be "https://sydle.com/loja/". Since the url does not start with baseUrl, then Vaadin believes
   * that this url does not belong to the router and does not handle navigation.
   *
   * In these cases, a trailing slash will be added.
   */
  const currentUrl = new URL(window.location.href);
  const baseUrl = new URL(getAbsoluteUrl());

  if (currentUrl.pathname + '/' === baseUrl.pathname) {
    currentUrl.pathname += '/';
  }

  window.history.replaceState(null, '', currentUrl);

  setGlobalTokens();
}

/**
 * Insert style tokens into html body using package definitions
 */
function setGlobalTokens(): void {
  const globalCSSTokens = getConfig<UIAppConfig>('ui-app')?.customTokens;

  if (!globalCSSTokens || Object.keys(globalCSSTokens).length === 0) {
    return;
  }

  // The duplicated :root selector is used here to increase specificity
  // and prevent the styles from being overridden by other styles.
  const css = jsonToCSS({ ':root:root': globalCSSTokens });

  const styleElement = document.createElement('style');
  styleElement.id = 'ui-app-global-css-tokens';
  styleElement.appendChild(document.createTextNode(css));

  document.head.appendChild(styleElement);
}

function jsonToCSS(json: Record<string, Record<string, string>>) {
  return Object.entries(json)
    .map(([selector, definition]) => {
      const rules = Object.entries(definition)
        .map(([rule, value]) => `${rule}:${value};`)
        .join('');

      return `${selector}{${rules}}`;
    })
    .join('\n');
}
